import React, { useContext, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { ApiContext } from '../../../../context/MyContext';
import { useUtils } from '../../../../context/UtilsContext';
import WorkoutEditorRow from '../shell/WorkoutEditorRow';
import ExerciseItemCard from '../exercise/ExerciseItemCard';
import ExerciseFormModal from '../exercise/ExerciseFormModal';
import WorkoutPeriodSummaryCard from './shell/WorkoutPeriodSummaryCard';
import useWorkoutHandlers from './useWorkoutHandlers';
import CopyExerciseToOtherDaysModal from '../exercise/CopyExerciseToOtherDaysModal';
import { copyExerciseToOtherPlans } from '../../core/utils/copyExerciseToOtherPlans';
import { sumExerciseCalories } from '../../core/utils/workoutDuration';

function WorkoutPeriodEditor({
  periodKey,
  periodLabel,
  templateId = 0,
  planId = 0,
  context,
  setAlertMessage,
  setAlertType,
  updateWorkoutGroupTypeIds,
  workoutGroupTypeIds,
  clientId = null,
  workoutBundle = null,
  refreshBundle,
  copyTargetDays = null,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editWorkout, setEditWorkout] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSequenceMode, setIsSequenceMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [copyWorkout, setCopyWorkout] = useState(null);

  const { get, post } = useContext(ApiContext);
  const { sessionUtils, workoutCommonUtils } = useUtils();
  const kgWeightUnitId =
    workoutCommonUtils?.weight_units?.find((u) => {
      const desc = (u.description || '').toLowerCase();
      return desc.includes('kg') || desc.includes('kilogram');
    })?.id ?? workoutCommonUtils?.weight_units?.[0]?.id;
  const eventTypes = sessionUtils?.event_types?.[0]?.event_types || [];
  const workout_group_types = workoutCommonUtils?.workout_group_types || [];
  const canCopyToOtherDays =
    context === 'plan' &&
    Array.isArray(copyTargetDays) &&
    copyTargetDays.some((d) => d.hasOtherPlan);

  const workout = useWorkoutHandlers({
    get,
    post,
    context,
    templateId,
    planId,
    periodKey,
    updateWorkoutGroupTypeIds,
    setAlertMessage,
    setAlertType,
    workoutGroupTypeIds,
    clientId,
    workoutBundle,
    refreshBundle,
  });

  const {
    savedWorkouts,
    setSavedWorkouts,
    hasFetchedRef,
    fetchSavedItems,
    applyPeriodFromBundle,
    reloadAfterMutation,
    handleSaveWorkout,
    handleDeleteWorkout,
    handleUpdateWorkout,
    handleExerciseSearch,
    handleUpdateSequenceOrder,
  } = workout;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    if (workoutBundle) {
      applyPeriodFromBundle(workoutBundle);
      return;
    }
    fetchSavedItems();
  }, [workoutBundle, fetchSavedItems, applyPeriodFromBundle, hasFetchedRef]);

  // Keep cards in sync with server bundle, but never while the coach is reordering
  // locally — re-applying the bundle would instantly undo move left/right.
  useEffect(() => {
    if (!workoutBundle || !hasFetchedRef.current || isSequenceMode) return;
    applyPeriodFromBundle(workoutBundle);
  }, [workoutBundle, applyPeriodFromBundle, hasFetchedRef, isSequenceMode]);

  const getNextSequence = () => {
    const orders = (savedWorkouts || []).map((w) => w.sequence_order || 0);
    return orders.length ? Math.max(...orders) + 1 : 1;
  };

  const formatSets = (sets) =>
    // Keep soft-deleted sets so updateWorkout can emit DELETE operations.
    sets.map((set) => {
      const unitId =
        set.weight_unit_id || set.target_weight_unit_id || kgWeightUnitId;
      const weight = Number(set.target_weight) || 0;
      const base =
        context === 'template'
          ? {
              reps: parseInt(set.reps, 10) || 0,
              time_seconds: parseInt(set.time_seconds, 10) || 0,
              rest_time_seconds: parseInt(set.rest_time_seconds, 10) || 0,
              target_weight: weight,
              weight_unit_id: unitId,
              target_weight_unit_id: unitId,
            }
          : {
              target_reps: parseInt(set.reps, 10) || 0,
              target_time_seconds: parseInt(set.time_seconds, 10) || 0,
              target_rest_seconds: parseInt(set.rest_time_seconds, 10) || 0,
              target_weight: weight,
              target_weight_unit_id: unitId,
              weight_unit_id: unitId,
            };
      return { ...base, id: set.id || null, is_deleted: set.is_deleted || false };
    });

  const handleSave = async (selected, details, sets) => {
    setSaving(true);
    try {
      const seq = getNextSequence();
      const ok = await handleSaveWorkout(
        { ...selected, sequence_number: seq },
        { ...details, sequence_number: seq },
        sets
      );
      if (ok) {
        setModalOpen(false);
        setEditWorkout(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (selected, details, sets) => {
    const groupTypeId = workoutGroupTypeIds?.[periodKey]?.id;
    if (!groupTypeId) {
      setAlertMessage?.(
        'Workout period is still loading. Please try again in a moment.'
      );
      setAlertType?.('error');
      return;
    }
    setSaving(true);
    try {
      const ok = await handleUpdateWorkout(selected, {
        sets: formatSets(sets),
        workoutRow: {
          group_type_id: groupTypeId,
          workout_id:
            editWorkout?.workout_id ||
            editWorkout?.exercise_id ||
            selected?.exercise_id ||
            selected?.workout_id,
          sequence_order:
            editWorkout?.sequence_order || selected?.sequence_number || 1,
          rest_time_after: details.workout_rest_time,
        },
      });
      if (ok) {
        setModalOpen(false);
        setEditWorkout(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const totalCalories = savedWorkouts.reduce(
    (sum, w) => sum + sumExerciseCalories(w),
    0
  );

  const handleCopyToOtherDays = async (selectedWeekdayIds) => {
    if (!copyWorkout) return;
    const planIds = selectedWeekdayIds
      .map((id) => {
        const day = copyTargetDays.find((d) => String(d.weekdayId) === String(id));
        return day?.targetPlanId;
      })
      .filter(Boolean);

    const result = await copyExerciseToOtherPlans({
      get,
      post,
      exercise: copyWorkout,
      periodKey,
      targetPlanIds: planIds,
      clientId,
      eventTypes,
      workout_group_types,
    });

    if (!result.success) {
      setAlertMessage?.(result.message || 'Failed to copy exercise.');
      setAlertType?.('error');
      throw new Error(result.message);
    }

    setAlertMessage?.(result.message || 'Exercise copied.');
    setAlertType?.(result.warning ? 'warning' : 'success');
  };

  return (
    <>
      <WorkoutEditorRow
        summaryCard={
          <WorkoutPeriodSummaryCard
            title={periodLabel}
            exerciseCount={savedWorkouts.length}
            caloriesLabel={totalCalories > 0 ? `${totalCalories.toFixed(0)} cal` : null}
            isSequenceMode={isSequenceMode}
            onEditSequence={() => {
              setOriginalOrder([...savedWorkouts]);
              setIsSequenceMode(true);
            }}
            onSaveSequence={async () => {
              const ok = await handleUpdateSequenceOrder(
                savedWorkouts,
                context,
                workoutGroupTypeIds,
                periodKey
              );
              if (ok) {
                setIsSequenceMode(false);
                setOriginalOrder([]);
                await reloadAfterMutation();
              }
            }}
            onCancelSequence={() => {
              setSavedWorkouts([...originalOrder]);
              setIsSequenceMode(false);
              setOriginalOrder([]);
            }}
          />
        }
      >
        <div className="w-full overflow-x-auto">
          <div className="flex gap-4 pb-2 min-w-min items-center">
            {savedWorkouts.map((w, idx) => (
              <div key={w.id || idx} className="flex-shrink-0">
                <ExerciseItemCard
                  workout={w}
                  onEdit={() => {
                    setEditWorkout(w);
                    setModalOpen(true);
                  }}
                  onDelete={() => handleDeleteWorkout(w.id)}
                  onCopyToOtherDays={
                    canCopyToOtherDays ? () => setCopyWorkout(w) : undefined
                  }
                  isSequenceMode={isSequenceMode}
                  onMoveLeft={() => {
                    if (idx === 0) return;
                    const next = [...savedWorkouts];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setSavedWorkouts(next);
                  }}
                  onMoveRight={() => {
                    if (idx >= savedWorkouts.length - 1) return;
                    const next = [...savedWorkouts];
                    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                    setSavedWorkouts(next);
                  }}
                  canMoveLeft={idx > 0}
                  canMoveRight={idx < savedWorkouts.length - 1}
                />
              </div>
            ))}
            {!isSequenceMode && (
              <button
                type="button"
                onClick={() => {
                  setEditWorkout(null);
                  setModalOpen(true);
                }}
                className="flex-shrink-0 flex items-center justify-center w-32 h-40 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-primary hover:bg-orange-50 transition-colors"
                title={`Add to ${periodLabel}`}
              >
                <Plus className="w-6 h-6 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </WorkoutEditorRow>

      <ExerciseFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditWorkout(null);
        }}
        isEditMode={!!editWorkout}
        initialWorkout={editWorkout}
        kgWeightUnitId={kgWeightUnitId}
        onSearch={handleExerciseSearch}
        onSave={editWorkout ? handleUpdate : handleSave}
        saving={saving}
      />

      <CopyExerciseToOtherDaysModal
        isOpen={Boolean(copyWorkout)}
        exerciseName={copyWorkout?.workout_type || copyWorkout?.workout_name}
        periodLabel={periodLabel}
        days={copyTargetDays || []}
        onConfirm={handleCopyToOtherDays}
        onClose={() => setCopyWorkout(null)}
      />
    </>
  );
}

export default WorkoutPeriodEditor;
